import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Box, Container, Typography, Grid, Button, Stack, Card } from '@mui/material';
import CallToAction from '../widgets/CallToAction';
import { keyframes } from '@mui/system';

export default function SolutionsEcoles() {


    // Align wording with student/enterprise pages
    const humanSectionBullets = [
        "Pilotez l'employabilité avec des indicateurs clairs",
        "Accompagnement personnalisé et à l'échelle",
        "CV et candidatures valorisées (vidéo, recommandations)",
        "Mises en relation qualifiées avec entreprises et alumni",
        "Confidentialité et conformité RGPD"
    ];

    // Helper for logo alt text (same approach as other pages)
    const altFromSrc = (s = '') =>
        (s.split('/').pop() || '')
            .replace(/\.[a-zA-Z0-9]+$/, '')
            .replace(/[-_]/g, ' ')
            .trim() || 'logo';

    const companyLogos = [
        '/assets/images/entreprises/accenture.png',
        '/assets/images/entreprises/bnp.jpg',
        '/assets/images/entreprises/Dassault.png',
        '/assets/images/entreprises/EDF.png',
        '/assets/images/entreprises/EY.png',
        '/assets/images/entreprises/puy-du-fou.png',
        '/assets/images/entreprises/toyota.png',
    ];

    // Fonctionnalités (alignées à la page étudiant, adaptées aux écoles)
    const featureShowcase = [
        {
            key: 'dashboard',
            title: "Tableau de bord employabilité",
            desc: "Suivez les candidatures, les offres pertinentes et les indicateurs d'insertion en temps réel.",
            image: "/assets/images/plateforme_school/6.png"
        },
        {
            key: 'matching-ia',
            title: 'Matching IA quotidien',
            desc: "Notre IA propose chaque jour des offres adaptées aux profils de vos étudiants après un entretien IA.",
            image: "/assets/images/plateforme_school/2.png"
        },
        {
            key: 'messagerie',
            title: 'Messagerie ',
            desc: 'Échanges fluides entre équipes, étudiants et entreprises dans un seul fil.',
            image: "/assets/images/plateforme_school/7.png"
        },
        {
            key: 'events',
            title: 'Événements ',
            desc: 'Planifiez forums, conférences et ateliers.',
            image: "/assets/images/plateforme_school/8.png"
        },
        {
            key: 'partner',
            title: 'Partenaires',
            desc: 'Développez votre réseau d’entreprises partenaires.',
            image: "/assets/images/plateforme_school/9.png"
        }
    ];

    const [selectedFeatureIndex, setSelectedFeatureIndex] = useState(0);

    // Infinite horizontal scroll animation (same spirit as other pages)
    const scrollX = keyframes`
        0% { transform: translateX(0); }
        100% { transform: translateX(-50%); }
    `;

    return (
        <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 } }}>
            <Helmet>
                <title>Écoles — Career center: employabilité, matching IA, partenariats | Student Link</title>
                <meta name="description" content="Pilotez l'employabilité: tableau de bord, matching IA, messagerie unifiée, événements et réseau alumni." />
                <link rel="canonical" href="https://studentlink.fr/ecoles" />
            </Helmet>
            {/* Hero section */}
            <Grid container spacing={4} alignItems="center" sx={{ mb: { xs: 8, md: 10 } }}>
                <Grid item xs={12} md={6}>
                    <Typography variant="h2" component="h1" gutterBottom sx={{ fontWeight: 700 }}>
                        Votre partenaire carrière tout-en-un.
                    </Typography>
                    <Typography variant="h6" color="text.secondary" sx={{ mb: 3, color: 'text.secondary', fontWeight: 400, maxWidth: 640 }}>
                        Une plateforme web + mobile pour booster l'insertion, fluidifier les échanges et valoriser vos étudiants.
                    </Typography>

                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                        <Button
                            component="a"
                            href="https://calendly.com/j-derosnay-studentlink/30min"
                            target="_blank"
                            rel="noopener noreferrer"
                            variant="contained"
                            size="large"
                            sx={{
                                backgroundColor: '#0075FF',
                                px: 3,
                                height: 48,
                                borderRadius: 4,
                                '&:hover': { backgroundColor: '#0056CC' }
                            }}
                        >
                            Demander une démo
                        </Button>
                        <Button
                            href="/ecoles/abonnements"
                            variant="outlined"
                            size="large"
                            sx={{ height: 48, px: 3, borderRadius: 4, fontWeight: 600 }}

                        >
                            Voir les abonnements
                        </Button>

                    </Stack>
                </Grid>
                <Grid item xs={12} md={6}>
                    {/* Placeholder visuel aligné avec la page étudiant */}
                    <Box sx={{
                        position: 'relative',
                        borderRadius: 8,
                        height: { xs: 300, md: 420 },
                        // background: 'linear-gradient(135deg,#0075FF 0%,#00B2FF 100%)',
                        // boxShadow: '0 10px 35px rgba(0,117,255,0.25)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        overflow: 'hidden'
                    }}>
                        <Box
                            component="img"
                            src="/assets/images/plateforme_school/placeholder.jpg"
                            alt="Interface entreprise Student Link"
                            loading="lazy"
                            decoding="async"
                            onError={(e) => { e.currentTarget.style.display = 'none'; }}
                            sx={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                                objectPosition: 'left center',
                                borderRadius: 8,
                            }}
                        />
                    </Box>
                </Grid>
            </Grid>

            {/* Fonctionnalités interactives */}
            <Box sx={{ mb: { xs: 6, md: 10 } }}>
                <Box sx={{ textAlign: 'center', mb: 4 }}>
                    <Typography variant="h3" sx={{ fontWeight: 700, fontSize: { xs: 32, md: 42 } }}>
                        Un logiciel intelligent dédié à l'insertion professionnelle
                    </Typography>

                </Box>
                <Grid container spacing={6} alignItems="stretch">
                    {/* Colonne gauche: liste cliquable */}
                    <Grid item xs={12} md={6}>
                        <Stack spacing={2} role="listbox" aria-label="Fonctionnalités école">
                            {featureShowcase.map((f, idx) => {
                                const active = idx === selectedFeatureIndex;
                                return (
                                    <Box
                                        key={f.key}
                                        onClick={() => setSelectedFeatureIndex(idx)}
                                        role="option"
                                        aria-selected={active}
                                        tabIndex={0}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' || e.key === ' ') {
                                                e.preventDefault();
                                                setSelectedFeatureIndex(idx);
                                            }
                                        }}
                                        sx={{
                                            p: 2.25,
                                            borderRadius: 8,
                                            border: '1px solid',
                                            borderColor: active ? 'primary.main' : 'divider',
                                            boxShadow: active ? '0 6px 18px rgba(0,117,255,0.12)' : 'none',
                                            cursor: 'pointer',
                                            transition: 'all .2s ease',
                                            '&:hover': {
                                                borderColor: 'primary.main',
                                                boxShadow: '0 6px 18px rgba(0,117,255,0.12)'
                                            }
                                        }}
                                    >
                                        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                            {f.title}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                            {f.desc}
                                        </Typography>
                                    </Box>
                                );
                            })}
                        </Stack>
                    </Grid>
                    {/* Colonne droite: aperçu illustré */}
                    <Grid item xs={12} md={6}>
                        <Box
                            sx={{
                                position: 'relative',
                                borderRadius: 8,
                                height: { xs: 260, md: '100%' },
                                minHeight: { md: 360 },
                                backgroundColor: '#f5f7fb',
                                border: '1px solid',
                                borderColor: 'divider',
                                overflow: 'hidden',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                            aria-live="polite"
                        >
                            <Box
                                component="img"
                                src={featureShowcase[selectedFeatureIndex].image}
                                alt={featureShowcase[selectedFeatureIndex].title}
                                loading="lazy"
                                decoding="async"
                                sx={{
                                    maxHeight: '100%',
                                    maxWidth: '100%',
                                    objectFit: 'contain'
                                }}
                            />
                        </Box>
                    </Grid>
                </Grid>
            </Box>

            {/* HUMAIN AU CENTRE */}
            <Box sx={{ mt: 10 }}>
                <Grid container spacing={4} alignItems="stretch">
                    {/* Colonne gauche: image handshake */}
                    <Grid item xs={12} md={6}>
                        <Box
                            component="img"
                            src="/assets/images/handshake.jpg"
                            alt="Poignée de main — collaboration"
                            loading="lazy"
                            decoding="async"
                            onError={(e) => {
                                e.currentTarget.onerror = null;
                                e.currentTarget.src = '/assets/images/team.jpg';
                            }}
                            sx={{
                                width: '100%',
                                height: { xs: 240, md: 420 },
                                objectFit: 'cover',
                                borderRadius: 8,
                                border: '1px solid',
                                borderColor: 'divider'
                            }}
                        />
                    </Grid>
                    {/* Colonne droite: contenu texte sans boîte */}
                    <Grid item xs={12} md={6} sx={{ display: 'flex', minHeight: { xs: 240, md: 420 } }}>
                        <Box sx={{ p: { xs: 0, md: 1 }, height: { xs: 'auto', md: '100%' }, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                            <Typography variant="h3" sx={{ fontWeight: 700, mt: 1, fontSize: { xs: 30, md: 40 } }}>L’humain avant tout</Typography>
                            <Typography variant="body1" sx={{ mt: 2, color: 'text.secondary' }}>
                                Nous proposons directement vos offres au bon candidats et facilitons les échanges pour que vous puissiez recruter le meilleur partenaire de danse.
                            </Typography>
                            <Grid container spacing={2} sx={{ mt: 3 }}>
                                {humanSectionBullets.map(b => (
                                    <Grid item xs={12} sm={6} key={b}>
                                        <Stack direction="row" spacing={1} alignItems="flex-start">
                                            <Box sx={{ fontSize: 18, lineHeight: '22px' }}>✅</Box>
                                            <Typography variant="body2" sx={{ lineHeight: 1.4 }}>{b}</Typography>
                                        </Stack>
                                    </Grid>
                                ))}
                            </Grid>
                        </Box>
                    </Grid>
                </Grid>
            </Box>



            {/* Final CTA */}
            <Box sx={{ mt: 10 }}>
                <CallToAction
                    title={"Prêt à améliorer l'insertion professionnelle de vos étudiants ?"}
                    subtitle={"Demandez une démonstration personnalisée de nos solutions."}
                    actions={(
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
                            <Button component="a" href="https://calendly.com/j-deservigny-studentlink/30min" target="_blank" rel="noopener noreferrer" variant="contained" sx={{ height: 48, borderRadius: 4, px: 2.5 }}>Demander une démo</Button>
                            <Button href="/ecoles/abonnements" variant="outlined" sx={{ height: 48, borderRadius: 4, px: 2.5 }}>Voir les abonnements</Button>
                        </Stack>
                    )}
                />
            </Box>
        </Container>
    );
}